import { Injectable } from '@angular/core';
import {
	EventName,
	ApplicationLoaded,
	ApplicationReady,
	ClosingRejected,
	HandleError,
	RxChangeImplantsViewAppParameters,
	RxPDFRequested,
	RxSaved,
	RxSend,
	RxSleeveConfirmationChanged,
	RxUpdatePlatformErrorTimeout,
	RxValidationFailed
} from '@itero/itero-client-communication';
import { EventBusService } from '@bootstrap/services/event-bus.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { handleErrorEventHeader, HandleErrorEventType } from '@bootstrap/consts/handle-error.consts';

export enum TargetApplication {
	All = '*',
	Platform = 'platform'
}

@Injectable({ providedIn: 'root' })
export class EventPublisherService {
	constructor(
		private eventBus: EventBusService,
		private logger: LoggerService
	) {}

	publishApplicationLoadedEvent() {
		return this.eventBus.publish<ApplicationLoaded>(EventName.appLoaded, [TargetApplication.All]);
	}

	publishApplicationReadyEvent() {
		return this.eventBus.publish<ApplicationReady>(EventName.appReady, [TargetApplication.All]);
	}

	publishCloseMe() {
		return this.eventBus.publish<CloseEvent>(EventName.closeMe, [TargetApplication.All]);
	}

	async publishError(payload: HandleError['data']) {
		try {
			return await this.eventBus.publish<HandleError>(EventName.handleError, [TargetApplication.Platform], payload);
		} catch (error) {
			const rawPayload = JSON.stringify(payload);

			this.logger.error(`Rx-App: unable to notify host about the error: ${rawPayload}.`, {
				module: EventPublisherService.name,
				error
			});
		}

		return false;
	}

	publishSavedEvent(rx: RxModel) {
		return this.eventBus
			.publish<RxSaved>(EventName.rxSaved, [TargetApplication.All], {
				rxId: rx.ID
			})
			.catch(err => this.handleError(err, HandleErrorEventType.SavedError));
	}

	publishSendEvent(rxId: string) {
		return this.eventBus
			.publish<RxSend>(EventName.rxSend, [TargetApplication.All], {
				rxId
			})
			.catch(err => this.handleError(err, HandleErrorEventType.SavedError));
	}

	public publishValidationFailureEvent(shouldStayInRx: boolean) {
		return this.eventBus
			.publish<RxValidationFailed>(EventName.rxValidationFailed, [TargetApplication.All], {
				stayInRx: shouldStayInRx
			})
			.catch(err => this.handleError(err, HandleErrorEventType.ValidationError));
	}

	public async publishSleeveConfirmationChangedEvent(isChecked: boolean) {
		return this.eventBus
			.publish<RxSleeveConfirmationChanged>(EventName.rxSleeveConfirmationChanged, [TargetApplication.All], {
				isChecked
			})
			.catch(err => this.handleError(err, HandleErrorEventType.SleeveConfirmationError));
	}

	public async publishUpdatePlatformErrorTimeout(requestedTimeout: number) {
		return this.eventBus
			.publish<RxUpdatePlatformErrorTimeout>(EventName.rxUpdateErrorTimeout, [TargetApplication.All], {
				requestedTimeout
			})
			.catch(err => this.handleError(err, HandleErrorEventType.UpdatePlatformErrorTimeoutError));
	}

	public publishRxChangeImplantsViewAppParameters(payload: RxChangeImplantsViewAppParameters['data']) {
		return this.eventBus
			.publish<RxChangeImplantsViewAppParameters>(EventName.rxChangeImplantsViewAppParameters, [TargetApplication.All], payload)
			.catch(err => this.handleError(err, HandleErrorEventType.RxChangeImplantsViewAppParametersError));
	}

	public async publishClosingRejected() {
		return this.eventBus
			.publish<ClosingRejected>(EventName.closingRejected, [TargetApplication.All])
			.catch(err => this.handleError(err, HandleErrorEventType.ClosingRejectedError));
	}

	public async publishPdfRequested(rx: Record<string, unknown>) {
		return this.eventBus
			.publish<RxPDFRequested>(EventName.rxPdfRequested, [TargetApplication.All], { rx })
			.catch(err => this.handleError(err, HandleErrorEventType.ClosingRejectedError));
	}

	private handleError(error: Error, errorId: HandleErrorEventType) {
		const message = error?.message ?? 'Something went wrong with running RxApp application.';
		const errorHeader = handleErrorEventHeader[errorId];

		return this.publishError({
			errorId,
			errorHeader,
			errorDescription: message
		});
	}
}
